/**
 * Panel 3: Asset Readiness — 4-node fingerprint deep dive
 *
 * Features:
 *   - 4-node fingerprint with sub-descriptions
 *   - MC Rate + Historical bar chart
 *   - MC Rate Forecast (30/60/90 day projection)
 *   - Operational Metrics with target thresholds
 *   - Enriched Sustainment Challenges with priority & recommendations
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { AssetDetail, AssetSummary, FuelState, AssetLoadout } from '../../../types'
import { getAssetDetail, getForceComposition, getAssetFuel, getAssetLoadout as fetchLoadout, formatPercent } from '../../../services/api'
import { FuelGauge } from '../logistics/FuelGauge'
import { AssetLoadoutCard } from '../logistics/AssetLoadoutCard'
import { AssetSilhouette } from '../../shared/AssetSilhouette'

interface AssetReadinessPanelProps {
  selectedAsset: string | null
}

// ── Fingerprint node descriptions ──────────────────────────────────────────
const FINGERPRINT_META: Record<string, { desc: string }> = {
  Readiness: { desc: 'MC rate, crew cert, training currency' },
  Platform:  { desc: 'Age, hours, avionics gen, upgrades' },
  Logistics: { desc: 'Supply days, parts avail, mx backlog' },
  Mission:   { desc: 'Sortie rate, effectiveness, ISR contrib' },
}

// ── Challenge classification ───────────────────────────────────────────────
type ChallengePriority = 'CRITICAL' | 'HIGH' | 'MEDIUM'

interface ClassifiedChallenge {
  text: string
  priority: ChallengePriority
  recommendation: string
}

const CHALLENGE_RULES: Array<{
  match: RegExp
  priority: ChallengePriority
  recommendation: string
}> = [
  {
    match: /MC rate.*(?:4[0-9]|[0-3]\d)%/i,
    priority: 'CRITICAL',
    recommendation: 'Initiate emergency depot-level surge maintenance; request AFMC waiver for cannibalization authority.',
  },
  {
    match: /MC rate.*(?:5[0-9])%/i,
    priority: 'HIGH',
    recommendation: 'Accelerate scheduled depot inputs; coordinate with DLA for priority parts allocation.',
  },
  {
    match: /MC rate.*(?:6[0-9])%/i,
    priority: 'HIGH',
    recommendation: 'Review unit-level maintenance backlog; prioritize MICAP resolution with supply chain.',
  },
  {
    match: /trend.*declining/i,
    priority: 'HIGH',
    recommendation: 'Conduct root-cause analysis on downward trend drivers; evaluate workforce sufficiency and skill gaps.',
  },
  {
    match: /trend.*volatile/i,
    priority: 'MEDIUM',
    recommendation: 'Stabilize supply pipeline; investigate intermittent failure modes driving rate swings.',
  },
  {
    match: /aging airframe/i,
    priority: 'CRITICAL',
    recommendation: 'Accelerate structural life-extension program (SLEP) or bridge recapitalization timeline; increase NDI intervals.',
  },
  {
    match: /diminishing manufacturing/i,
    priority: 'HIGH',
    recommendation: 'Engage reverse-engineering / additive manufacturing alternatives via AFRL rapid prototyping.',
  },
  {
    match: /parts obsolescence/i,
    priority: 'HIGH',
    recommendation: 'Expand DMS&T sourcing; fund form-fit-function replacement qualification with OEM partnerships.',
  },
  {
    match: /below mission capable goal/i,
    priority: 'MEDIUM',
    recommendation: 'Review unit sortie scheduling to balance mission demand with maintenance recovery windows.',
  },
]

function classifyChallenge(text: string): ClassifiedChallenge {
  for (const rule of CHALLENGE_RULES) {
    if (rule.match.test(text)) {
      return { text, priority: rule.priority, recommendation: rule.recommendation }
    }
  }
  // Default fallback
  return {
    text,
    priority: 'MEDIUM',
    recommendation: 'Monitor trend and coordinate with MAJCOM logistics cell for mitigation options.',
  }
}

const PRIORITY_CONFIG: Record<ChallengePriority, { bg: string; border: string; text: string; label: string }> = {
  CRITICAL: { bg: 'bg-red-500/15', border: 'border-red-500/40', text: 'text-red-400', label: 'CRIT' },
  HIGH:     { bg: 'bg-amber-500/15', border: 'border-amber-500/40', text: 'text-amber-400', label: 'HIGH' },
  MEDIUM:   { bg: 'bg-sky-500/10', border: 'border-sky-500/30', text: 'text-sky-400', label: 'MED' },
}

// ── MC Rate Forecast Logic ─────────────────────────────────────────────────
interface ForecastPoint {
  label: string
  rate: number
  meetsGoal: boolean
}

function computeForecast(
  currentRate: number,
  goal: number,
  trend: string,
  historicalRates: Record<string, number>,
): ForecastPoint[] {
  // Derive a monthly delta from historical data when possible
  const years = Object.keys(historicalRates).sort()
  let monthlyDelta = 0

  if (years.length >= 2) {
    const first = historicalRates[years[0]]
    const last = historicalRates[years[years.length - 1]]
    const spanYears = parseInt(years[years.length - 1]) - parseInt(years[0])
    if (spanYears > 0) {
      monthlyDelta = (last - first) / (spanYears * 12)
    }
  }

  // Adjust based on qualitative trend
  switch (trend) {
    case 'declining':
      // Use computed delta if negative, otherwise assume -0.3%/month
      if (monthlyDelta >= 0) monthlyDelta = -0.003
      break
    case 'stable':
    case 'stable_high':
      monthlyDelta = monthlyDelta * 0.3 // dampen
      break
    case 'stable_declining':
      if (monthlyDelta >= 0) monthlyDelta = -0.0015
      break
    case 'improving':
      if (monthlyDelta <= 0) monthlyDelta = 0.002
      break
    case 'volatile':
    case 'volatile_low':
      // Keep computed delta but add uncertainty (for display we just use delta)
      if (monthlyDelta >= 0) monthlyDelta = -0.002
      break
    default:
      // fallback: use computed
      break
  }

  const horizons = [
    { label: '30d', months: 1 },
    { label: '60d', months: 2 },
    { label: '90d', months: 3 },
  ]

  return horizons.map(h => {
    const projected = Math.max(0, Math.min(1, currentRate + monthlyDelta * h.months))
    return { label: h.label, rate: projected, meetsGoal: projected >= goal }
  })
}

// ── Operational Metric Target Logic ────────────────────────────────────────
interface MetricThreshold {
  target: string
  status: 'good' | 'warn' | 'bad' | 'neutral'
}

function getMetricThreshold(
  label: string,
  value: number,
  assignedCount: number,
  mcRateGoal: number,
): MetricThreshold {
  switch (label) {
    case 'Available': {
      const target = Math.round(assignedCount * mcRateGoal)
      const met = value >= target
      return { target: `>= ${target}`, status: met ? 'good' : 'bad' }
    }
    case 'In Maintenance': {
      const target = Math.round(assignedCount * (1 - mcRateGoal))
      const met = value <= target
      return { target: `<= ${target}`, status: met ? 'good' : 'bad' }
    }
    case 'Sorties/Period':
      return { target: '--', status: 'neutral' }
    case 'Effectiveness': {
      const effectivenessTarget = 0.85
      const met = value >= effectivenessTarget
      return { target: '>= 85.0%', status: met ? 'good' : value >= 0.7 ? 'warn' : 'bad' }
    }
    case 'Crew/Aircraft': {
      const crewTarget = 1.5
      const met = value >= crewTarget
      return { target: '>= 1.5', status: met ? 'good' : 'warn' }
    }
    case 'Turnaround': {
      const turnaroundTarget = 240 // 4.0 hrs = 240 min default
      const met = value <= turnaroundTarget
      return { target: '<= 240 min', status: met ? 'good' : value <= 360 ? 'warn' : 'bad' }
    }
    case 'O&M/FH':
      return { target: '--', status: 'neutral' }
    default:
      return { target: '--', status: 'neutral' }
  }
}

const THRESHOLD_COLORS: Record<string, string> = {
  good: '#22c55e',
  warn: '#f59e0b',
  bad: '#ef4444',
  neutral: '#6b7280',
}

// ============================================================================
// Main Component
// ============================================================================

export function AssetReadinessPanel({ selectedAsset }: AssetReadinessPanelProps) {
  const [asset, setAsset] = useState<AssetDetail | null>(null)
  const [assetList, setAssetList] = useState<AssetSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeAsset, setActiveAsset] = useState<string | null>(selectedAsset)
  const [fuelState, setFuelState] = useState<FuelState | null>(null)
  const [loadout, setLoadout] = useState<AssetLoadout | null>(null)

  // Fetch asset list for selector
  useEffect(() => {
    getForceComposition().then(res => {
      if (res.data) setAssetList(res.data.assets)
    })
  }, [])

  const fetchDetail = useCallback(async (assetId: string) => {
    setLoading(true)
    setError(null)
    setFuelState(null)
    setLoadout(null)
    try {
      const [res, fuelRes, loadoutRes] = await Promise.all([
        getAssetDetail(assetId),
        getAssetFuel(assetId).catch(() => ({ data: undefined, error: undefined })),
        fetchLoadout(assetId).catch(() => ({ data: undefined, error: undefined })),
      ])
      if (res.error) throw new Error(res.error)
      if (res.data) setAsset(res.data)
      if (fuelRes.data) setFuelState(fuelRes.data)
      if (loadoutRes.data) setLoadout(loadoutRes.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch asset detail')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedAsset) {
      setActiveAsset(selectedAsset)
      fetchDetail(selectedAsset)
    }
  }, [selectedAsset, fetchDetail])

  useEffect(() => {
    if (activeAsset) fetchDetail(activeAsset)
  }, [activeAsset, fetchDetail])

  // ── Derived data ──────────────────────────────────────────────────────────
  const forecast = useMemo(() => {
    if (!asset) return []
    return computeForecast(asset.mc_rate, asset.mc_rate_goal, asset.mc_rate_trend, asset.mc_rates_historical)
  }, [asset])

  const classifiedChallenges = useMemo(() => {
    if (!asset) return []
    return asset.sustainment_challenges.map(classifyChallenge).sort((a, b) => {
      const order: Record<ChallengePriority, number> = { CRITICAL: 0, HIGH: 1, MEDIUM: 2 }
      return order[a.priority] - order[b.priority]
    })
  }, [asset])

  // ── Render: no selection ──────────────────────────────────────────────────
  if (!activeAsset) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-4xl opacity-30">$</div>
        <div className="text-lg font-semibold text-term-text">Asset Readiness Fingerprint</div>
        <div className="text-sm text-term-textDim">Select an asset to view its 4-node fingerprint</div>
        <div className="grid grid-cols-4 gap-2 mt-4">
          {assetList.map(a => (
            <button key={a.id} onClick={() => setActiveAsset(a.id)} className="term-box p-2 text-xs text-left hover:bg-term-bgLight/50 transition-colors cursor-pointer">
              <div className="font-bold text-term-text">{a.asset_type}</div>
              <div className="text-term-textDim">{formatPercent(a.mc_rate)} MC</div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-pulse text-term-textDim">Loading {activeAsset}...</div></div>
  }

  if (error || !asset) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-term-error text-lg font-semibold">Error</div>
        <div className="text-term-textDim text-sm">{error || 'No data'}</div>
        <button onClick={() => fetchDetail(activeAsset)} className="term-button term-button-primary">Retry</button>
      </div>
    )
  }

  const fp = asset.fingerprint
  const metrics = asset.operational_metrics

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AssetSilhouette assetType={asset.asset_type} width={56} height={22} color={asset.domain === 'AIR' ? '#3b82f6' : asset.domain === 'SEA' ? '#06b6d4' : asset.domain === 'SPACE' ? '#a855f7' : asset.domain === 'CYBER' ? '#22c55e' : '#f59e0b'} className="opacity-70 shrink-0" />
          <div>
            <div className="text-lg font-bold text-term-text">{asset.asset_type}</div>
            <div className="text-sm text-term-textDim">{asset.role} | {asset.domain} | {asset.assigned_count} assigned ({asset.real_fleet_size} total fleet)</div>
          </div>
        </div>
        <select
          value={activeAsset}
          onChange={e => setActiveAsset(e.target.value)}
          className="text-xs bg-term-bgDark border border-term-border rounded px-2 py-1 text-term-text focus:outline-none"
        >
          {assetList.map(a => <option key={a.id} value={a.id}>{a.asset_type}</option>)}
        </select>
      </div>

      {/* ── 4-Node Fingerprint Visualization (enhanced with descriptions) ── */}
      <div className="term-box p-4">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-3">
          4-Node Asset Fingerprint
        </div>
        <div className="grid grid-cols-4 gap-4">
          <FingerprintNode label="Readiness" value={fp.readiness} weight="30%" color="#3b82f6" />
          <FingerprintNode label="Platform" value={fp.platform} weight="25%" color="#8b5cf6" />
          <FingerprintNode label="Logistics" value={fp.logistics} weight="25%" color="#f59e0b" />
          <FingerprintNode label="Mission" value={fp.mission} weight="20%" color="#22c55e" />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-term-textDim">Composite Score:</span>
          <span className="text-lg font-bold text-term-text">{asset.readiness_score.toFixed(1)}/100</span>
        </div>
      </div>

      {/* ── MC Rate + Historical ── */}
      <div className="grid grid-cols-2 gap-4">
        <div className="term-box p-4">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">MC Rate</div>
          <div className="text-3xl font-bold" style={{ color: asset.mc_rate < 0.5 ? '#ef4444' : asset.mc_rate < asset.mc_rate_goal ? '#f59e0b' : '#22c55e' }}>
            {formatPercent(asset.mc_rate)}
          </div>
          <div className="text-xs text-term-textDim mt-1">Goal: {formatPercent(asset.mc_rate_goal)} | Trend: {asset.mc_rate_trend}</div>
          <div className="text-xs text-term-textDim mt-1">Avg Aircraft Age: {asset.avg_aircraft_age} years</div>

          {/* Historical MC rates */}
          {Object.keys(asset.mc_rates_historical).length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] text-term-textDim uppercase mb-1">Historical MC Rates</div>
              <div className="flex gap-1 items-end h-16">
                {Object.entries(asset.mc_rates_historical).sort(([a], [b]) => a.localeCompare(b)).map(([year, rate]) => {
                  const h = Math.max(4, (rate as number) * 60)
                  const color = (rate as number) < 0.5 ? '#ef4444' : (rate as number) < 0.65 ? '#f59e0b' : '#22c55e'
                  return (
                    <div key={year} className="flex flex-col items-center gap-0.5 flex-1">
                      <div className="w-full rounded-t" style={{ height: h, backgroundColor: color, minWidth: 8 }} title={`${year}: ${((rate as number) * 100).toFixed(0)}%`} />
                      <div className="text-[8px] text-term-textDim">{year.slice(2)}</div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Operational Metrics with thresholds ── */}
        <div className="term-box p-4">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">Operational Metrics</div>
          <div className="space-y-1.5 text-sm">
            <MetricRowThreshold
              label="Available"
              value={String(metrics.assets_available)}
              threshold={getMetricThreshold('Available', metrics.assets_available, asset.assigned_count, asset.mc_rate_goal)}
            />
            <MetricRowThreshold
              label="In Maintenance"
              value={String(metrics.assets_in_maintenance)}
              threshold={getMetricThreshold('In Maintenance', metrics.assets_in_maintenance, asset.assigned_count, asset.mc_rate_goal)}
            />
            <MetricRowThreshold
              label="Sorties/Period"
              value={String(metrics.sorties_per_period)}
              threshold={getMetricThreshold('Sorties/Period', metrics.sorties_per_period, asset.assigned_count, asset.mc_rate_goal)}
            />
            <MetricRowThreshold
              label="Effectiveness"
              value={formatPercent(metrics.mission_effectiveness)}
              threshold={getMetricThreshold('Effectiveness', metrics.mission_effectiveness, asset.assigned_count, asset.mc_rate_goal)}
            />
            <MetricRowThreshold
              label="Crew/Aircraft"
              value={String(metrics.crew_per_aircraft)}
              threshold={getMetricThreshold('Crew/Aircraft', metrics.crew_per_aircraft, asset.assigned_count, asset.mc_rate_goal)}
            />
            <MetricRowThreshold
              label="Turnaround"
              value={`${metrics.turnaround_min} min`}
              threshold={getMetricThreshold('Turnaround', metrics.turnaround_min, asset.assigned_count, asset.mc_rate_goal)}
            />
            {metrics.o_and_m_per_fh > 0 && (
              <MetricRowThreshold
                label="O&M/FH"
                value={`$${metrics.o_and_m_per_fh.toLocaleString()}`}
                threshold={getMetricThreshold('O&M/FH', metrics.o_and_m_per_fh, asset.assigned_count, asset.mc_rate_goal)}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── MC Rate Forecast (30/60/90 day) ── */}
      <div className="term-box p-4">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">
          MC Rate Forecast
        </div>
        <div className="grid grid-cols-3 gap-3">
          {forecast.map(f => (
            <div
              key={f.label}
              className="term-box p-3 flex flex-col items-center gap-1"
              style={{ borderColor: f.meetsGoal ? '#22c55e40' : '#ef444440' }}
            >
              <div className="text-[10px] text-term-textDim uppercase font-medium">{f.label}</div>
              <div
                className="text-xl font-bold tabular-nums"
                style={{ color: f.meetsGoal ? '#22c55e' : f.rate >= asset.mc_rate_goal * 0.9 ? '#f59e0b' : '#ef4444' }}
              >
                {(f.rate * 100).toFixed(1)}%
              </div>
              <div className="flex items-center gap-1">
                {f.meetsGoal ? (
                  <span className="text-[10px] text-green-400 font-medium">MEETS GOAL</span>
                ) : (
                  <span className="text-[10px] text-red-400 font-medium">BELOW GOAL</span>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[10px] text-term-textDim italic">
          Projection based on TRM-C2 model with current sustainment trajectory
        </div>
      </div>

      {/* ── Fuel & Loadout Status ── */}
      {(fuelState || loadout) && (
        <div className="grid grid-cols-2 gap-4">
          {fuelState && (
            <div className="term-box p-4">
              <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">Fuel Status</div>
              <FuelGauge fuelPct={fuelState.fuel_pct} status={fuelState.status} timeToBingo={fuelState.time_to_bingo_hrs} />
              <div className="mt-2 space-y-1 text-[11px]">
                <div className="flex justify-between">
                  <span className="text-term-textDim">Current</span>
                  <span className="text-term-text tabular-nums">{fuelState.current_lbs.toLocaleString()} lbs</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-term-textDim">Capacity</span>
                  <span className="text-term-text tabular-nums">{fuelState.capacity_lbs.toLocaleString()} lbs</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-term-textDim">Burn Rate</span>
                  <span className="text-term-text tabular-nums">{fuelState.burn_rate_lbs_hr.toLocaleString()} lbs/hr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-term-textDim">Sorties Remaining</span>
                  <span className="text-term-text tabular-nums font-bold">{fuelState.sorties_remaining}</span>
                </div>
              </div>
            </div>
          )}
          {loadout && <AssetLoadoutCard loadout={loadout} />}
          {!fuelState && loadout && <div />}
          {fuelState && !loadout && <div />}
        </div>
      )}

      {/* ── Enriched Sustainment Challenges ── */}
      {classifiedChallenges.length > 0 && (
        <div className="term-box p-4">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-3">
            Sustainment Challenges
          </div>
          <div className="space-y-2">
            {classifiedChallenges.map((c, i) => {
              const cfg = PRIORITY_CONFIG[c.priority]
              return (
                <div key={i} className={`${cfg.bg} border ${cfg.border} rounded p-2.5`}>
                  <div className="flex items-start gap-2">
                    <span
                      className={`${cfg.text} text-[10px] font-bold border ${cfg.border} rounded px-1.5 py-0.5 whitespace-nowrap flex-shrink-0`}
                    >
                      {cfg.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-medium ${cfg.text}`}>
                        {c.text}
                      </div>
                      <div className="text-[11px] text-term-textDim mt-1 leading-snug">
                        <span className="text-term-text font-medium">REC: </span>
                        {c.recommendation}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function FingerprintNode({ label, value, weight, color }: { label: string; value: number; weight: string; color: string }) {
  const filled = Math.round(value / 10)
  const meta = FINGERPRINT_META[label]
  return (
    <div className="flex flex-col items-center gap-1.5 term-box p-3">
      <div className="text-xs font-medium text-term-textDim uppercase">{label}</div>
      {meta && (
        <div className="text-[9px] text-term-textDim/70 text-center leading-tight px-1" style={{ opacity: 0.65 }}>
          {meta.desc}
        </div>
      )}
      <div className="text-2xl font-bold" style={{ color }}>{value.toFixed(0)}</div>
      <div className="flex gap-[2px]">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="w-2 h-2 rounded-sm" style={{ backgroundColor: i < filled ? color : '#3a3a5c40' }} />
        ))}
      </div>
      <div className="text-[10px] text-term-textDim">Weight: {weight}</div>
    </div>
  )
}

function MetricRowThreshold({
  label,
  value,
  threshold,
}: {
  label: string
  value: string
  threshold: MetricThreshold
}) {
  const dotColor = THRESHOLD_COLORS[threshold.status]
  return (
    <div className="flex items-center gap-2">
      <span className="text-term-textDim text-xs flex-1 min-w-0 truncate">{label}</span>
      <span className="text-term-text font-medium tabular-nums text-xs">{value}</span>
      <div className="flex items-center gap-1 min-w-[72px] justify-end">
        {threshold.target !== '--' && (
          <>
            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />
            <span className="text-[10px] tabular-nums" style={{ color: dotColor }}>
              {threshold.target}
            </span>
          </>
        )}
        {threshold.target === '--' && (
          <span className="text-[10px] text-term-textDim">--</span>
        )}
      </div>
    </div>
  )
}
